/**
 * Light, dark, or the machine's own setting.
 *
 * The choice also lives in Settings, beside the illustration choice, which is
 * where a person goes to change how the application looks for good. This is
 * for the other case: it is dark outside, or the screen is in sunlight, and
 * the change is wanted now.
 *
 * It sits in the account menu, where a phone keeps it too, rather than as a
 * ninth control in the header row — the row had run out of width and wrapped,
 * which put one icon on a line of its own under the organization.
 *
 * What it changes is the same thing the settings page changes, through the
 * same hook, so the two can never disagree.
 */

import { useCallback, useRef, useState } from "react";

import { type ThemeChoice, useTheme } from "../lib/theme.ts";
import { IconButton, classes } from "./primitives.tsx";
import { useDismiss } from "./use-dismiss.ts";

const LABEL: Record<ThemeChoice, string> = {
  system: "Match the machine",
  light: "Light",
  dark: "Dark",
};

const ORDER: readonly ThemeChoice[] = ["system", "light", "dark"];

/** The three choices as menu rows, for a menu that is already open. */
export function ThemeChoices({ onChosen }: { readonly onChosen?: () => void } = {}) {
  const { choice, setChoice } = useTheme();
  return (
    <div className="border-t border-divider pt-1">
      <p className="px-2 py-1 text-muted">Appearance</p>
      {ORDER.map((value) => (
        <button
          key={value}
          type="button"
          role="menuitemradio"
          aria-checked={choice === value}
          className={classes(
            "flex min-h-11 w-full items-center gap-2 rounded-arc px-2 py-1 text-left hover:bg-selected",
            choice === value ? "font-semibold text-accent" : "",
          )}
          onClick={() => {
            setChoice(value);
            // The menu that held this closes: it covers the page, and the
            // choice is made.
            onChosen?.();
          }}
        >
          <span aria-hidden="true" className="w-3">
            {choice === value ? "•" : ""}
          </span>
          {LABEL[value]}
        </button>
      ))}
    </div>
  );
}

export function ThemeMenu() {
  const { choice, resolved, setChoice } = useTheme();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(box, open, close);

  return (
    <div ref={box} className="relative">
      <IconButton
        // The word is in the label, not on the screen: the header is a row of
        // words already, and this one is read far more often than it is used.
        icon={resolved === "dark" ? "moon" : "sun"}
        label={`Appearance: ${LABEL[choice]}`}
        ariaExpanded={open}
        ariaHasPopup="menu"
        onClick={() => setOpen(!open)}
      />
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 flex w-48 flex-col gap-1 rounded-arc border border-divider bg-paper p-2 text-sm shadow"
        >
          {ORDER.map((value) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={choice === value}
              className={classes(
                "flex items-center gap-2 rounded-arc px-2 py-1 text-left hover:bg-selected",
                choice === value ? "font-semibold text-accent" : "",
              )}
              onClick={() => {
                setChoice(value);
                close();
              }}
            >
              <span aria-hidden="true" className="w-3">
                {choice === value ? "•" : ""}
              </span>
              {LABEL[value]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
